import { Utensils } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { eur, techSlots, slotDays, mealAt, mealSum, mealTotals } from "@/lib/pricing";

const short = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const lastDay = (q) => q.teardown_date || q.event_date_end || q.show_days?.[q.show_days.length - 1]?.date || "";
const dayLabel = (q, d, kind) => {
  if (kind === "setup") return q.setup_date ? short(q.setup_date) : "Giorno prima";
  if (kind === "teardown") return lastDay(q) ? short(lastDay(q)) : "Ultimo giorno";
  const iso = q.show_days?.[d]?.date;
  return iso ? short(iso) : `G${d + 1}`;
};
const GROUPS = [["event", "Tecnici evento"], ["setup", "Tecnici allestimento"], ["teardown", "Supporto smontaggio"]];

const DayChip = ({ label, kind, d, has, toggle }) => (
  <div className="rounded border border-slate-700 px-2 py-1 text-xs flex items-center gap-2 bg-slate-950/40">
    <span className="font-mono text-slate-300">{label}</span>
    {[["lunch", "Pranzo"], ["dinner", "Cena"]].map(([k, l]) => (
      <label key={k} className="flex items-center gap-1 cursor-pointer">
        <Checkbox className="h-3.5 w-3.5" checked={has(k, d)} onCheckedChange={() => toggle(k, d)} data-testid={`meal-${kind}-${k}-day-${d}`} />{l}
      </label>
    ))}
  </div>
);

export const MealsPanel = ({ q, update }) => {
  const techs = techSlots(q);
  const first = mealAt(q, 0);
  const allMeals = () => Array.from({ length: Math.max(techs.length, (q.meals || []).length) }, (_, j) => mealAt(q, j));
  const members = (kind) => techs.map((t, i) => [t, i]).filter(([t]) => t.kind === kind);
  const on = (m, k, d) => m[k] && !(m[`skip_${k}`] || []).includes(d);
  const has = (kind) => (k, d) => members(kind).every(([, i]) => on(mealAt(q, i), k, d));
  const toggle = (kind) => (k, d) => {
    const turnOn = !has(kind)(k, d);
    const meals = allMeals();
    members(kind).forEach(([t, i]) => {
      const m = meals[i];
      const n = slotDays(q, t);
      const skip = m[k] ? (m[`skip_${k}`] || []) : Array.from({ length: n }, (_, x) => x);
      const next = turnOn ? skip.filter((x) => x !== d) : [...new Set([...skip, d])];
      meals[i] = { ...m, [k]: next.length < n, [`skip_${k}`]: next };
    });
    update({ meals });
  };
  const setPrice = (k, v) => update({ meals: allMeals().map((m) => ({ ...m, [`${k}_price`]: v })) });
  const groupTotal = (kind) => members(kind).reduce((s, [t, i]) => s + mealSum(mealAt(q, i), slotDays(q, t)), 0);
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-4" data-testid="meals-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 mr-auto"><Utensils className="h-5 w-5 text-sky-400" />Pasti</h2>
        {techs.length > 0 && [["lunch", "Pranzo"], ["dinner", "Cena"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm text-slate-400">{l} €
            <Input type="number" min={0} step="0.5" value={first[`${k}_price`]} onChange={(e) => setPrice(k, Math.max(+e.target.value || 0, 0))} className="h-8 w-20 font-mono" data-testid={`meal-${k}-price`} />
          </label>
        ))}
      </div>
      {techs.length === 0 && <p className="text-xs text-slate-500" data-testid="meals-empty">Aggiungi i tecnici nella sezione RISORSE UMANE: i pasti si calcolano per tutti.</p>}
      {techs.length > 0 && <p className="text-xs text-slate-500">Spunta pranzo e cena per giorno: valgono per tutti i tecnici del gruppo. Togli la spunta nei giorni in cui il pasto lo offre il cliente.</p>}
      {GROUPS.filter(([kind]) => members(kind).length).map(([kind, label]) => {
        const n = kind === "event" ? Math.max(+q.days || 1, 1) : 1;
        return (
          <div key={kind} className="border-t border-slate-800/60 pt-3 space-y-2" data-testid={`meal-group-${kind}`}>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium">{label}</span>
              <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300" data-testid={`meal-group-count-${kind}`}>× {members(kind).length}</span>
              <span className="ml-auto font-mono" data-testid={`meal-group-total-${kind}`}>{eur(groupTotal(kind))}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: n }, (_, d) => <DayChip key={d} label={dayLabel(q, d, kind)} kind={kind} d={d} has={has(kind)} toggle={toggle(kind)} />)}
            </div>
          </div>
        );
      })}
      {techs.length > 0 && (
        <p className="text-right text-sm text-slate-300">Totale pasti <b className="font-mono text-sky-300" data-testid="meals-total">{eur(mealTotals(q))}</b></p>
      )}
    </section>
  );
};
